import { memo, useState } from 'react';
import styled from 'styled-components';
import { Popover, Spin } from 'antd';
import { PiImage, PiMapTrifold, PiGridFour } from 'react-icons/pi';
import { MapData_Base } from '~/common/typescript/models/MapData';
import { API_URL_ADMIN } from '~/config/config';

interface LayerThumbnailProps {
  data: MapData_Base;
}

const LayerThumbnail = ({ data }: LayerThumbnailProps) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');

  const docOrId = data.documentId || data.id;
  const thumbnailUrl = docOrId ? `${API_URL_ADMIN}/api/map-data-ext/document-id/${docOrId}/thumbnail` : '';

  let isRaster = false;
  try {
    if (data.metadata) {
      const parsed = typeof data.metadata === 'string' ? JSON.parse(data.metadata) : data.metadata;
      isRaster = !!parsed.isRaster || !!parsed.gdalInfo;
    }
  } catch {
    // fallback
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    // Strapi fallback transparent pixel is 1x1
    if (img.naturalWidth <= 1 && img.naturalHeight <= 1) {
      setImageState('error');
    } else {
      setImageState('loaded');
    }
  };

  const handleImageError = () => {
    setImageState('error');
  };

  const popoverContent = (
    <div style={{ width: 220, padding: '4px 2px' }}>
      <div style={{ width: '100%', height: 140, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <img
          src={thumbnailUrl}
          alt={data.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {data.name}
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ 
          padding: '1px 6px', 
          borderRadius: 4, 
          fontSize: 10, 
          fontWeight: 600, 
          backgroundColor: isRaster ? '#fef3c7' : '#e0f2fe',
          color: isRaster ? '#92400e' : '#0369a1'
        }}>
          {isRaster ? 'Raster' : 'Vector'}
        </span>
        <span>ID: {data.id}</span>
      </div>
    </div>
  );

  return (
    <UI_ThumbWrapper>
      {thumbnailUrl && imageState !== 'error' && (
        <Popover
          content={imageState === 'loaded' ? popoverContent : null}
          trigger="hover"
          placement="right"
          mouseEnterDelay={0.3}
        >
          <UI_ThumbContainer $loaded={imageState === 'loaded'}>
            <UI_Img
              src={thumbnailUrl}
              alt={data.name}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: imageState === 'loaded' ? 'block' : 'none' }}
            />
            {imageState === 'loading' && (
              <UI_Placeholder $isRaster={isRaster}>
                <Spin size="small" />
              </UI_Placeholder>
            )}
          </UI_ThumbContainer>
        </Popover>
      )}

      {(imageState === 'error' || !thumbnailUrl) && (
        <UI_FallbackSwatch $isRaster={isRaster} title={isRaster ? 'Raster Layer' : 'Vector Layer'}>
          {isRaster ? <PiGridFour size={14} color="#d97706" /> : <PiMapTrifold size={14} color="#0284c7" />}
        </UI_FallbackSwatch>
      )}
    </UI_ThumbWrapper>
  );
};

export default memo(LayerThumbnail);

const UI_ThumbWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex-shrink: 0;
`;

const UI_ThumbContainer = styled.div<{ $loaded: boolean }>`
  width: 28px;
  height: 24px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #d1d5db;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2563eb;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    transform: scale(1.05);
  }
`;

const UI_Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UI_Placeholder = styled.div<{ $isRaster: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.$isRaster ? '#fffbeb' : '#f0f9ff')};
`;

const UI_FallbackSwatch = styled.div<{ $isRaster: boolean }>`
  width: 24px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid ${(props) => (props.$isRaster ? '#fcd34d' : '#bae6fd')};
  background-color: ${(props) => (props.$isRaster ? '#fef3c7' : '#e0f2fe')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
`;
